import { useState, useEffect } from 'react';
import { flushSync } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Moon, Sun, X, ChevronRight, Palette, Settings } from 'lucide-react';
import { createPortal } from 'react-dom';
import { useTheme } from '@/hooks/use-theme';

export function SettingsModal() {
  const [isOpen, setIsOpen] = useState(false);
  const { theme, setTheme } = useTheme();
  const [mounted, setMounted] = useState(false);
  const [activeTab, setActiveTab] = useState<'menu' | 'theme'>('menu');

  useEffect(() => { setMounted(true); }, []);
  if (!mounted) return null;

  const handleThemeChange = (newTheme: string, event?: React.MouseEvent) => {
    if (!event || !('startViewTransition' in document)) {
      setTheme(newTheme as 'light' | 'dark');
      return;
    }
    const button = event.currentTarget as HTMLElement;
    const rect = button.getBoundingClientRect();
    const x = rect.left + rect.width / 2;
    const y = rect.top + rect.height / 2;
    const maxRadius = Math.hypot(
      Math.max(x, window.innerWidth - x),
      Math.max(y, window.innerHeight - y)
    );
    document.documentElement.style.setProperty('--theme-toggle-x', `${x}px`);
    document.documentElement.style.setProperty('--theme-toggle-y', `${y}px`);
    document.documentElement.style.setProperty('--theme-toggle-radius', `${maxRadius}px`);
    const transition = (document as any).startViewTransition(() => {
      flushSync(() => { setTheme(newTheme as 'light' | 'dark'); });
    });
    transition.finished.then(() => {
      document.documentElement.style.removeProperty('--theme-toggle-x');
      document.documentElement.style.removeProperty('--theme-toggle-y');
      document.documentElement.style.removeProperty('--theme-toggle-radius');
    });
  };

  const isDark = theme === 'dark';

  return (
    <>
      {/* Mobile */}
      <button
        onClick={() => { setIsOpen(true); setActiveTab('menu'); }}
        className="lg:hidden rounded-lg p-2 text-[#999] hover:text-[#666] dark:text-[#555] dark:hover:text-[#888] hover:bg-[#f0f0f0] dark:hover:bg-[#1a1a1a] transition-colors"
      >
        <Settings className="size-5" strokeWidth={1.5} />
      </button>

      {/* Desktop - Minimal toggle */}
      <div className="hidden lg:flex items-center">
        <div className="flex h-8 rounded-lg bg-[#f0f0f0] dark:bg-[#1a1a1a] p-0.5">
            <button
            onClick={(e) => handleThemeChange('light', e)}
            className={`flex items-center justify-center w-8 rounded-md transition-all duration-200 ${
              !isDark
                ? 'bg-white dark:bg-[#333] text-[#1bb8f5] shadow-sm'
                : 'text-[#999] dark:text-[#555] hover:text-[#666] dark:hover:text-[#888]'
            }`}
            title="Aydınlık"
          >
            <Sun className="size-3.5" strokeWidth={1.5} />
          </button>
          <button
            onClick={(e) => handleThemeChange('dark', e)}
            className={`flex items-center justify-center w-8 rounded-md transition-all duration-200 ${
              isDark
                ? 'bg-white dark:bg-[#333] text-[#1bb8f5] dark:text-[#1bb8f5] shadow-sm'
                : 'text-[#999] dark:text-[#555] hover:text-[#666] dark:hover:text-[#888]'
            }`}
            title="Karanlık"
          >
            <Moon className="size-3.5" strokeWidth={1.5} />
          </button>
        </div>
      </div>

      {/* Mobile Modal */}
      {mounted && createPortal(
        <AnimatePresence>
          {isOpen && (
            <>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={() => setIsOpen(false)}
                className="fixed inset-0 z-[9999] bg-black/20 backdrop-blur-sm lg:hidden"
              />
              <motion.div
                initial={{ opacity: 0, scale: 0.96, y: "50%", x: "-50%" }}
                animate={{ opacity: 1, scale: 1, y: "-50%", x: "-50%" }}
                exit={{ opacity: 0, scale: 0.96, y: "50%", x: "-50%" }}
                className="fixed left-1/2 top-1/2 z-[9999] w-[90%] max-w-sm rounded-xl bg-white dark:bg-[#1a1a1a] shadow-xl ring-1 ring-black/[0.04] dark:ring-white/[0.06] lg:hidden overflow-hidden"
              >
                {/* Header */}
                <div className="flex items-center justify-between px-5 py-3.5 border-b border-[#e5e5e5] dark:border-[#333]">
                  <div className="flex items-center gap-2">
                    {activeTab !== 'menu' && (
                      <button onClick={() => setActiveTab('menu')} className="mr-1 text-[#999] hover:text-[#666] dark:text-[#555] dark:hover:text-[#888]">
                        <ChevronRight className="size-4 rotate-180" />
                      </button>
                    )}
                    <h2 className="text-[15px] font-semibold text-[#0a0a0a] dark:text-white">
                      {activeTab === 'menu' ? 'Ayarlar' : 'Görünüm'}
                    </h2>
                  </div>
                  <button onClick={() => setIsOpen(false)} className="rounded-md p-1 text-[#999] hover:text-[#666] hover:bg-[#f5f5f5] dark:text-[#555] dark:hover:text-[#888] dark:hover:bg-[#222] transition-colors">
                    <X className="size-4" />
                  </button>
                </div>

                <div className="p-2">
                  <AnimatePresence mode="wait">
                    {activeTab === 'menu' ? (
                      <motion.div key="menu" initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -10 }} className="space-y-0.5">
                        <button
                          onClick={() => setActiveTab('theme')}
                          className="w-full flex items-center justify-between p-3 rounded-lg hover:bg-[#f5f5f5] dark:hover:bg-[#222] transition-colors"
                        >
                          <div className="flex items-center gap-3">
                            <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-[#f0f0f0] dark:bg-[#2a2a2a]">
                              <Palette className="size-4 text-[#666] dark:text-[#888]" strokeWidth={1.5} />
                            </div>
                            <div className="text-left">
                              <div className="text-[13px] font-medium text-[#0a0a0a] dark:text-[#ededed]">Görünüm</div>
                              <div className="text-[11px] text-[#999] dark:text-[#666]">
                                {isDark ? 'Karanlık' : 'Aydınlık'}
                              </div>
                            </div>
                          </div>
                          <ChevronRight className="size-4 text-[#ccc] dark:text-[#444]" />
                        </button>
                      </motion.div>
                    ) : (
                      <motion.div key="theme" initial={{ opacity: 0, x: 10 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 10 }} className="space-y-0.5">
                        {[
                          { key: 'light', label: 'Aydınlık', icon: <Sun className="size-4" strokeWidth={1.5} /> },
                          { key: 'dark', label: 'Karanlık', icon: <Moon className="size-4" strokeWidth={1.5} /> },
                        ].map((t) => (
                          <button
                            key={t.key}
                            onClick={(e) => handleThemeChange(t.key, e)}
                            className={`w-full flex items-center justify-between p-3 rounded-lg transition-colors ${
                              theme === t.key
                                ? 'bg-[#f0f0f0] dark:bg-[#2a2a2a]'
                                : 'hover:bg-[#f5f5f5] dark:hover:bg-[#222]'
                            }`}
                          >
                            <div className="flex items-center gap-3">
                              <div className={`flex items-center justify-center w-8 h-8 rounded-lg ${
                                theme === t.key
                                  ? 'bg-[#1bb8f5] text-white'
                                  : 'bg-[#f0f0f0] dark:bg-[#2a2a2a] text-[#999] dark:text-[#666]'
                              }`}>
                                {t.icon}
                              </div>
                              <span className={`text-[13px] font-medium ${
                                theme === t.key ? 'text-[#1bb8f5]' : 'text-[#666] dark:text-[#888]'
                              }`}>
                                {t.label}
                              </span>
                            </div>
                            {theme === t.key && (
                              <div className="size-1.5 rounded-full bg-[#1bb8f5]" />
                            )}
                          </button>
                        ))}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </motion.div>
            </>
          )}
        </AnimatePresence>,
        document.body
      )}
    </>
  );
}
